import { useLocalStorage } from '@mantine/hooks';
import { GetListResponse, HttpError, useDeleteMany, useList, useUpdate } from '@refinedev/core';
import { QueryObserverResult } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import {
  Frequency,
  useTotalForFrequency,
  calculateAnnualAmount,
  calculateMonthlyAmount,
} from '../utils';
import { Dispatch, SetStateAction } from 'react';

export type IncomeFrequency = 'Monthly' | 'Weekly' | 'Annually' | 'One-off';
export type IncomeType =
  | 'Income'
  | 'Benefit'
  | 'Employment'
  | 'ShareOrDividend'
  | 'Rental'
  | 'Pension';

export type ApiIncome = {
  id: string;
  description: string;
  frequency: IncomeFrequency;
  amountPreTax: number;
  amountPostTax: number;
  incomeType: IncomeType;
  clientId: string;
};

export type Income = ApiIncome & {
  amountPreTaxAnnually: number;
  amountPostTaxAnnually: number;
  amountPreTaxMonthly: number;
  amountPostTaxMonthly: number;
};

export type EditableIncome = Omit<ApiIncome, 'id'> & {
  id?: string;
};

type UseIncomesReturnType = QueryObserverResult<GetListResponse<ApiIncome>, HttpError> & {
  incomesData: Income[] | undefined;
  upsertIncomes: (items: EditableIncome[]) => Promise<void>;
  deleteIncomes: (ids: string[]) => Promise<void>;
  frequency: Frequency;
  setFrequency: Dispatch<SetStateAction<Frequency>>;
  totalPreTax: number;
  totalPostTax: number;
};

export default function useIncomes(): UseIncomesReturnType {
  const { clientId } = useCurrentClient();
  const [frequency, setFrequency] = useLocalStorage<Frequency>({
    key: 'cashflow-incomes-frequency',
    defaultValue: 'monthly',
  });

  const result = useList<ApiIncome>({
    resource: `clients/${clientId}/incomes`,
  });
  const incomesData = result.data?.data?.map(income => ({
    ...income,
    amountPreTaxAnnually: calculateAnnualAmount(income.amountPreTax, income.frequency),
    amountPostTaxAnnually: calculateAnnualAmount(income.amountPostTax, income.frequency),
    amountPreTaxMonthly: calculateMonthlyAmount(income.amountPreTax, income.frequency),
    amountPostTaxMonthly: calculateMonthlyAmount(income.amountPostTax, income.frequency),
  }));

  const { mutateAsync: upsertIncomesMutation } = useUpdate<EditableIncome>({
    resource: `clients/${clientId}/incomes`,
    id: 'batch',
  });

  const upsertIncomes = async (items: EditableIncome[]) => {
    await upsertIncomesMutation({
      values: items.map(item => ({
        ...item,
        clientId,
      })),
    });

    await result.refetch();
  };

  const { mutateAsync: deleteIncomesMutation } = useDeleteMany({});
  const deleteIncomes = async (ids: string[]) => {
    await deleteIncomesMutation({
      resource: `clients/${clientId}/incomes`,
      ids,
    });
  };

  const totalPreTax = useTotalForFrequency({
    items: incomesData,
    frequency,
    frequencyField: 'frequency',
    amountField: 'amountPreTax',
  });

  const totalPostTax = useTotalForFrequency({
    items: incomesData,
    frequency,
    frequencyField: 'frequency',
    amountField: 'amountPostTax',
  });

  return {
    incomesData,
    upsertIncomes,
    deleteIncomes,
    frequency,
    setFrequency,
    totalPreTax,
    totalPostTax,
    ...result,
  };
}
